<template>
  <div class="app">
    <slot />
    <Notifications />
    <Modal
      :isOpen.sync="confirmModal.isOpen"
      v-on:close="confirmModalClosed"
      id="confirm-modal"
      :title="null"
    >
      <div class="confirm-modal">
        <div class="icon" v-if="confirmModal.options.icon">
          <i class="fa" :class="confirmModal.options.icon"></i>
        </div>
        <h3 class="title" v-text="confirmModal.options.title"></h3>
        <p
          v-if="confirmModal.options.message"
          class="message"
          v-text="confirmModal.options.message"
        ></p>
        <div class="buttons">
          <button
            type="button"
            class="btn btn-danger-outline"
            @click="closeConfirmModal"
          >
            {{ confirmModal.options.cancelText }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="confirmModalConfirmed"
          >
            {{ confirmModal.options.confirmText }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
<script>
import "@fortawesome/fontawesome-free/css/all.css";
import Notifications from "./components/notifications/notifications";

const seo = {
  title: defaultSeo.title,
  description: defaultSeo.description,
  keywords: defaultSeo.keywords,
  image: defaultSeo.image,
  canonical: defaultSeo.canonical,
  locale: defaultSeo.locale,
  robots: defaultSeo.robots,
  "og:site_name": defaultSeo["og:site_name"],
  "og:type": defaultSeo["og:type"],
  "og:url": defaultSeo["og:url"],
};

const confirmModalDefaultOptions = {
  isOpen: false,
  title: "Are you sure?",
  message: "",
  confirmText: "Yes, I'm sure",
  cancelText: "Cancel",
  onConfirm: null,
  onCancel: null,
  icon: "fa-question-circle",
};

export default {
  components: {
    Notifications,
  },
  watch: {
    "$page.props.flash": {
      immediate: true,
      handler(data) {
        if (data.use_sweetalert) {
          if (data.success_msg) {
            this.success_alert(data.success_msg);
          }
          if (data.error_msg) {
            this.error_alert(data.error_msg);
          }
          if (data.info_msg) {
            this.info_alert(data.info_msg);
          }
        }
      },
    },
    "$page.props.seoData": {
      immediate: true,
      handler(data) {
        if (data) {
          this.meta = { ...seo, ...data };
        } else {
          this.meta = seo;
        }
        this.updateSeoTags();
      },
    },
  },
  data() {
    return {
      meta: seo,
      matchMediaObject: null,
      sideNavOpen: false,
      confirmModal: {
        isOpen: false,
        options: confirmModalDefaultOptions,
      },
    };
  },
  mounted() {
    this.matchMediaObject = window.matchMedia("(min-width: 1055px)");
    this.mediQueryChange(this.matchMediaObject);

    this.matchMediaObject.addEventListener("change", (event) =>
      this.mediQueryChange(event)
    );

    this.$root.$on("confirmModal", (data) => {
      this.openConfirmModal(data);
    });
  },
  methods: {
    mediQueryChange(screen) {
      this.$store.dispatch("viewportUpdate", screen.matches);
    },
    updateSeoTags() {
      // titles
      document.title = this.meta.title;
      this.selectAndUpdateTags(
        [
          'meta[property="og:title"]',
          'meta[property="twitter:title"]',
          'meta[name="twitter:title"]',
        ],
        this.meta.title
      );

      // description
      this.selectAndUpdateTags(
        [
          'meta[name="description"]',
          'meta[property="og:description"]',
          'meta[property="twitter:description"]',
          'meta[name="twitter:description"]',
        ],
        this.meta.description
      );

      // keywords
      this.selectAndUpdateTags(
        [
          'meta[name="keywords"]',
          'meta[property="og:keywords"]',
          'meta[property="twitter:keywords"]',
          'meta[name="twitter:keywords"]',
        ],
        this.meta.keywords
      );

      // image
      this.selectAndUpdateTags(
        [
          'meta[property="og:image"]',
          'meta[property="twitter:image"]',
          'meta[name="twitter:image"]',
          'meta[name="twitter:image:src"]',
        ],
        this.meta.image
      );

      // canonical
      this.selectAndUpdateTags(
        ['link[rel="canonical"]', 'meta[property="og:url"]'],
        this.meta.canonical
      );

      // locale
      this.selectAndUpdateTags(
        [
          'meta[property="og:locale"]',
          'meta[property="og:locale:alternate"]',
          'meta[property="og:locale:alternate"]',
        ],
        this.meta.locale
      );
      document.querySelector("html").setAttribute("lang", this.meta.locale);

      // robots
      this.selectAndUpdateTags(['meta[name="robots"]'], this.meta.robots);

      // og:site_name
      this.selectAndUpdateTags(
        ['meta[property="og:site_name"]'],
        this.meta["og:site_name"]
      );

      // og:type
      this.selectAndUpdateTags(
        ['meta[property="og:type"]'],
        this.meta["og:type"]
      );

      // og:url
      this.selectAndUpdateTags(
        ['meta[property="og:url"]'],
        this.meta["og:url"]
      );
    },
    selectAndUpdateTags(tags = [], value) {
      tags.forEach((element) => {
        const ele = document.querySelector(element);
        if (ele) {
          ele.setAttribute("content", value);
        } else {
          // create new meta tag
          const meta = document.createElement("meta");
          meta.setAttribute("content", value);
          meta.setAttribute(
            "name",
            element.replace("meta[", "").replace("]", "")
          );
          document.head.appendChild(meta);
        }
      });
    },
    openConfirmModal(options = {}) {
      this.confirmModal.isOpen = true;
      this.confirmModal.options = { ...confirmModalDefaultOptions, ...options };
    },
    confirmModalClosed() {
      if (this.confirmModal.isOpen) {
        this.confirmModal.isOpen = false;
      }

      if (
        this.confirmModal.options.onCancel &&
        typeof this.confirmModal.options.onCancel === "function"
      ) {
        this.confirmModal.options.onCancel();
        this.confirmModal.options.onCancel = null;
      }

      setTimeout(() => {
        this.confirmModal.options = confirmModalDefaultOptions;
      }, 200);
    },
    closeConfirmModal() {
      if (
        this.confirmModal.options.onCancel &&
        typeof this.confirmModal.options.onCancel === "function"
      ) {
        this.confirmModal.options.onCancel();
      }

      this.confirmModal.options.onCancel = null;

      this.confirmModalClosed();
    },
    confirmModalConfirmed() {
      if (
        this.confirmModal.options.onConfirm &&
        typeof this.confirmModal.options.onConfirm === "function"
      ) {
        this.confirmModal.options.onConfirm();
      }

      this.confirmModal.options.onCancel = null;

      this.confirmModalClosed();
    },
  },
};
</script>
<style lang="scss" scoped>
.confirm-modal {
  text-align: center;
  --icon-size: 5rem;
  position: relative;
  .icon {
    width: var(--icon-size);
    height: var(--icon-size);
    font-size: calc(var(--icon-size) / 2);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--primary);
    color: var(--primary-contrast);
    margin: 0 auto;
    margin-bottom: 1.2rem;
  }
  .title {
    font-size: 1.8rem;
    color: var(--dark);
    margin-bottom: 0.5rem;
  }
  .message {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    color: var(--dark);
    opacity: 0.6;
  }
  .buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    text-align: center;
    margin-top: 2.6rem;
  }
}
</style>